import React, { useEffect, useState, useRef, useContext } from "react";
// jquery
import $ from "jquery";
// React Bootstrap
import { Form, Navbar, Col, FormControl, Button } from "react-bootstrap";
// Componentes
import TablaDocumentos from "../../generic/TablaDocumentos";
import FormAñadirDocumento from "../../generic/FormAñadirDocumento";
// Custom spinner
import CustomSpinner from "../../generic/CustomSpinner";
// Styled Components
import styled from "styled-components";
// Hooks
import { useIsMount } from "../../hooks/UseIsMount";
import useObserver from "../../hooks/UseObserver";
import { useDebounce } from "../../hooks/UseDebounce";
// Utils
import { downloadFile, objectToFormData, removeEmptyFields } from "../../../utils";
// Contexto
import { AlertBoxContext } from "../../../context/alertBoxContext";
import { ModalContext } from "../../../context/modalContext";

const CustomNavBar = styled(Navbar)`
  min-width: 200px;
  padding: 0px;
  margin: 0px;
  padding-top: 10px;
`;

const CustomTable = styled.div`
  position: relative;
  overflow: auto;
  max-height: 500px;
`;

const MisionDocumentos = ({ idProvincia, idMision, canAddConvenio }) => {
  // Spinner
  const [loading, setLoading] = useState(false);

  // Contexto
  const { modalSetShow, modalSetTitle, modalSetContent } = useContext(ModalContext);

  // Alertas
  const { addAlert } = useContext(AlertBoxContext);

  // Vida religiosa del usuario
  const [misionDocumentos, setMisionDocumentos] = useState([]);

  // Filtros, paginación y observador
  const [page, setPage] = useState(1); // Página por la que va
  const [lastPageReached, setLastPageReached] = useState(false); // ultima pagina alcanzada
  const [reload, setReload] = useState(false); // Reload para recargar el observador
  const [findName, setFindName] = useState(""); // Filtro para el  buscador
  const isMount = useIsMount(); // Se renderiza por primera vez?
  const scrollingDiv = useRef(null); // Div que contendrá el scroll

  // Resetea el explorador con los filtros actuales
  const resetMisionDocumentos = () => {
    setPage(1);
    setReload(!reload);
  };

  // Componente para hacer la busqueda cuando pasen 300 ms
  useDebounce(() => {
    if (!isMount) {
      resetMisionDocumentos();
    }
    scrollingDiv.current.scrollLeft = 0;
  }, [findName]);

  useEffect(() => {
    handleGetMisionDocumentos();
  }, [reload]);

  // **************************************************** GET ************************************************
  // Obtiene la vida religiosa de un usuario
  const getMisionDocumentos = () => {
    let settings = {
      url: `${process.env.REACT_APP_API_URL}/provincias/${idProvincia}/misiones/${idMision}/documentos?page=${page}&type=File&findName=${findName}`,
      method: "GET",
      xhrFields: {
        withCredentials: true,
      },
    };
    return $.ajax(settings);
  };

  // Manejador de la respuesta de la vida religiosa
  const handleGetMisionDocumentos = (addToExisting = false) => {
    setPage(page + 1);
    setLoading(true);
    getMisionDocumentos()
      .then((response) => {
        if (response.totalPages <= page) {
          setLastPageReached(true);
        } else {
          setLastPageReached(false);
        }
        if (addToExisting) {
          setMisionDocumentos([...misionDocumentos, ...response.items]);
        } else {
          setMisionDocumentos(response.items);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  // **************************************************** POST ************************************************
  // Petición para añadir un documento
  const postMisionDocumento = (data) => {
    let settings = {
      url: `${process.env.REACT_APP_API_URL}/provincias/${idProvincia}/misiones/${idMision}/documentos?type=File`,
      method: "POST",
      xhrFields: {
        withCredentials: true,
      },
      processData: false,
      contentType: false,
      data: data,
    };
    return $.ajax(settings);
  };

  // Manejador para la petición de añadir documentos
  const handlePostMisionDocumento = (data) => {
    setLoading(true);
    modalSetShow(false);
    removeEmptyFields(data);
    const formData = objectToFormData(data);
    postMisionDocumento(formData)
      .then((response) => {
        setMisionDocumentos([...misionDocumentos, response]);
        setLoading(false);
        addAlert("Éxito", "Se ha añadido el documento correctamente", "success");
      })
      .catch(() => {
        setLoading(false);
        addAlert("Error", "No se ha podido añadir correctamente el documento", "danger");
      });
  };

  // **************************************************** DELETE ************************************************
  // Petición para eliminar un documento
  const deleteMisionDocumento = (idDocumento) => {
    let settings = {
      url: `${process.env.REACT_APP_API_URL}/provincias/${idProvincia}/misiones/${idMision}/documentos/${idDocumento}`,
      method: "DELETE",
      xhrFields: {
        withCredentials: true,
      },
    };
    return $.ajax(settings);
  };

  // Manejador para el post de la imagen de una misión
  const handleDeleteMisionDocumento = (idDocumento) => {
    setLoading(true);
    deleteMisionDocumento(idDocumento)
      .then((response) => {
        // Eliminamos el documento del estado
        let miDocumento = misionDocumentos.find((documento) => documento.id === idDocumento);
        let miMisionDocumentos = [...misionDocumentos];
        miMisionDocumentos.splice(miMisionDocumentos.indexOf(miDocumento), 1);
        setMisionDocumentos(miMisionDocumentos);
        setLoading(false);
        addAlert("Exito", "Se ha borrado el documento correctamente", "success");
      })
      .catch(() => {
        setLoading(false);
        addAlert("Error", "No se ha podido eliminar correctamente el documento", "danger");
      });
  };

  // **************************************************** UTILS ************************************************
  // Para manejar el filtro del cargo
  const handleChangeFindName = (e) => {
    setFindName(e.target.value);
  };

  // Para abrir el documento en una nueva pestaña
  const handleDownloadDocumento = async (documento) => {
    let response = await downloadFile(`/provincias/${idProvincia}/misiones/${idMision}/documentos/${documento.id}`);
    if (!response) {
      addAlert("Aviso", "No se ha encontrado el archivo en el sistema", "warning");
    }
  };

  // **************************************************** OBSERVADOR ************************************************
  const loadMore = async () => {
    if (!lastPageReached && !loading) {
      handleGetMisionDocumentos(true);
    }
  };

  const target = useRef(null);

  useObserver(
    loadMore,
    {
      root: scrollingDiv.current,
      rootMargin: "0px",
      threshold: 1,
    },
    target,
    misionDocumentos
  );

  return (
    <div className="position-relative">
      <CustomSpinner animation={"border"} loading={loading} />
      {/* Filtros */}
      <CustomNavBar expand="md">
        {/* Cuando se hace pequeña la pantalla */}
        <Navbar.Toggle>
          <span className="align-text-bottom"> Filtros</span>
        </Navbar.Toggle>
        <Navbar.Collapse>
          <Form as="div" className="w-100">
            <Form.Row>
              <Form.Group as={Col} xs={12} md>
                <div className={"d-flex "}>
                  <FormControl className="mr-1" type="text" onChange={handleChangeFindName} placeholder={"Búsqueda"} />
                </div>
              </Form.Group>
            </Form.Row>
          </Form>
        </Navbar.Collapse>
      </CustomNavBar>

      <CustomTable ref={scrollingDiv}>
        <TablaDocumentos misionDocumentos={misionDocumentos} handleDeleteMisionDocumento={handleDeleteMisionDocumento} handleDownloadDocumento={handleDownloadDocumento} />
        <br ref={target} id="observable" />
      </CustomTable>
      <Button
        onClick={() => {
          modalSetShow(true);
          modalSetTitle("Añadir documento");
          modalSetContent(<FormAñadirDocumento submissionHandler={handlePostMisionDocumento} validation={canAddConvenio} />);
        }}
        className="d-flex ml-auto"
      >
        Añadir documento
      </Button>
    </div>
  );
};

export default MisionDocumentos;
